import React, { useEffect, useState } from 'react'
import moment from 'moment'
import { Alert, Chip, Stack, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import API from '../../../../services/API'
import { useNavigate } from 'react-router-dom'

export const statusChip = (status) => (
  <Chip
    size="small"
    label={status}
    color={
      status === 'Resolved'
        ? 'success'
        : status === 'Under Investigation'
          ? 'warning'
          : 'error'
    }
  />
)

const Discrepancies = () => {
  const [discrepancies, setDiscrepancies] = useState([])
  const [statusFilter, setStatusFilter] = useState('unresolved')
  const [fetchError, setFetchError] = useState(false)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    API.get('/medsmx/discrepancies')
      .then((res) => {
        if (res.status === 200) {
          setDiscrepancies(res.data)
        } else {
          setFetchError(true)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [])

  const rows =
    statusFilter === 'unresolved'
      ? discrepancies.filter((row) => row.status !== 'Resolved')
      : statusFilter === 'all'
        ? discrepancies
        : discrepancies.filter((row) => row.status === statusFilter)

  return (
    <Stack gap={1}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="title2">Medicine Discrepancies</Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={statusFilter}
          onChange={(e, newValue) => newValue && setStatusFilter(newValue)}
        >
          <ToggleButton value="unresolved">Unresolved</ToggleButton>
          <ToggleButton value="Resolved">Resolved</ToggleButton>
          <ToggleButton value="all">All</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      {fetchError && (
        <Alert severity="error">
          Unable to load discrepancies. Please try again later.
        </Alert>
      )}
      <CustomDataGrid
        norowsoverlay={
          <Alert severity="info">
            No discrepancies here. A discrepancy record is opened automatically
            whenever a bag audit or controlled drug audit finds a count that
            doesn't match the expected stock.
          </Alert>
        }
        rows={rows}
        getRowId={(params) => params._id}
        disableRowSelectionOnClick
        onRowClick={({ row }) =>
          navigate(`/medicines-management/discrepancies/${row._id}`)
        }
        loading={loading}
        columns={[
          {
            field: 'createdAt',
            renderCell: ({ row }) =>
              moment(row.createdAt).format('DD/MM/YYYY HH:mm'),
            valueGetter: (value) => moment(value).toISOString(),
            headerName: 'Raised',
            minWidth: 150,
            flex: 0.5,
          },
          {
            field: 'source',
            headerName: 'Found During',
            minWidth: 140,
            renderCell: ({ row }) =>
              row.source === 'cdAudit' ? 'CD audit' : 'Bag audit',
          },
          {
            field: 'where',
            headerName: 'Where',
            minWidth: 200,
            flex: 1,
            valueGetter: (value, row) =>
              row.location?.name ??
              [
                row.employee?.userAccount?.firstName,
                row.employee?.userAccount?.lastName,
              ]
                .filter(Boolean)
                .join(' '),
          },
          {
            field: 'details',
            headerName: 'Medicines',
            minWidth: 220,
            flex: 1,
            valueGetter: (value, row) =>
              (row.details ?? []).map((detail) => detail.title).join(', '),
          },
          {
            field: 'isControlled',
            headerName: 'CD',
            minWidth: 70,
            align: 'center',
            headerAlign: 'center',
            display: 'flex',
            renderCell: ({ row }) =>
              row.isControlled ? (
                <Chip size="small" color="error" label="CD" />
              ) : null,
          },
          {
            field: 'status',
            headerName: 'Status',
            minWidth: 160,
            display: 'flex',
            renderCell: ({ row }) => statusChip(row.status),
          },
          {
            field: 'assignedTo',
            headerName: 'Assigned To',
            minWidth: 160,
            valueGetter: (value, row) =>
              [row.assignedTo?.firstName, row.assignedTo?.lastName]
                .filter(Boolean)
                .join(' '),
          },
        ]}
      />
    </Stack>
  )
}

export default Discrepancies
